import React, { useContext, useEffect, useRef, useState } from "react";
import { FormContext } from "../form_buttons";
import { Autocomplete, TextField } from "@mui/material";
import { TypeConf, useType } from "../../types";
import { Box } from "@mui/system";
import { fieldMarginBottom } from "../../form_constants";

interface FormAutoCompleteProp<T> {
    field: string;
    label: string;
    type: TypeConf<T>;
    helpText?: string;
    fieldIdName?: string;
    value?: T;
    readOnly?: boolean;
    disabled?: boolean;
    onChange?: (type: TypeConf<T>, value: T | null) => void;
    onKeyUp?: (event: React.KeyboardEvent<HTMLDivElement>, inputValue: string) => void;
}

function FormAutocomplete<T>(props: FormAutoCompleteProp<T>) {
    const { field, label, type, helpText, onChange, onKeyUp, fieldIdName = "id", value, readOnly, disabled } = props;

    const inputRef = useRef<HTMLInputElement>(null);
    const typer = useType(type);

    const [elements, setElements] = useState<T[]>([]);
    const [selectedItem, setSelectedItem] = useState<T>(value ?? ({} as T));
    const [userInput, setUserInput] = useState<string>("");

    // Versión para descartar respuestas obsoletas de peticiones anteriores
    const requestVersionRef = useRef(0);

    const formContext = useContext(FormContext);
    const model = formContext.form;
    const modelField = model.field(field);

    model.register(
        field,
        () => (selectedItem && (selectedItem as any)[fieldIdName] ? selectedItem : undefined),
        t => setSelectedItem(t ?? ({} as T)),
    );

    const labeler = (item: T | undefined | null): string => {
        if (!item || !(item as any).id) return "";
        return nilToEmpty(type.labeler(item));
    };

    const valuer = (item: T | undefined | null): string =>
        nilToEmpty(item ? type.valuer(item)?.toString() : "");

    const nilToEmpty = (v: any): string => (v ? v.toString() : "");

    const changeSelectedItem = (item: T | undefined | null) => {
        setSelectedItem(item && (item as any).id ? item : ({} as T));
    };

    useEffect(() => {
        changeSelectedItem(modelField.value() ?? value);
        const version = ++requestVersionRef.current;
        typer.supplier().then(array => {
            if (version === requestVersionRef.current) setElements(array);
        });
    }, [value]);

    useEffect(() => {
        const version = ++requestVersionRef.current;
        typer.supplier(userInput).then(array => {
            if (version === requestVersionRef.current) setElements(array);
        });
    }, [userInput]);

    const handleChange = (_: React.SyntheticEvent, newValue: T | null) => {
        changeSelectedItem(newValue);
        model.notifyFieldChange(field, newValue ?? undefined);
        onChange?.(type, newValue);
    };

    return (
        <Box className={["form-input", "form-input-" + field].join(" ")} mb={fieldMarginBottom}>
            {elements && (
                <Autocomplete
                    fullWidth
                    id={field}
                    value={selectedItem}
                    options={elements}
                    disablePortal
                    readOnly={readOnly}
                    disabled={disabled}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(option, val) => valuer(option) === valuer(val)}
                    getOptionLabel={option => labeler(option)}
                    onChange={handleChange}
                    onInputChange={(_e, val) => setUserInput(val)}
                    renderOption={(optProps, option) => (
                        <li {...optProps} key={(option as any)[fieldIdName] ?? valuer(option)}>
                            {labeler(option)}
                        </li>
                    )}
                    renderInput={params => (
                        <TextField
                            {...params}
                            label={label}
                            fullWidth
                            helperText={helpText}
                            value={labeler(selectedItem)}
                            onKeyUp={onKeyUp ? e => onKeyUp(e, inputRef.current?.value || "") : undefined}
                            inputRef={inputRef}
                            inputProps={{ ...params.inputProps }}
                        />
                    )}
                />
            )}
        </Box>
    );
}

export default FormAutocomplete;
